/**
 * Real POI icon registry for vector point symbology.
 *
 * Maki is the icon set Mapbox/MapLibre basemaps use for points of interest, and
 * Temaki extends it with the long tail (both CC0-1.0, so no attribution
 * plumbing). Together they provide ~770 named symbols.
 *
 * That is far too much data for the initial bundle, so the sets are dynamically
 * imported the first time a picker opens or an icon symbol is rendered, then
 * cached for the session. Engines that need an icon inside a synchronous style
 * callback use {@link peekPointIcon} and re-apply once {@link loadPointIcons}
 * resolves.
 *
 * Every Iconify body paints with `currentColor`, so recolouring is a string
 * substitution rather than an SVG parse.
 */
/**
 * The slice of Iconify's `IconifyJSON` this module reads. Declared locally
 * rather than imported from `@iconify/types`: that package is barred by
 * `geosoft/icon-library-monoculture`, and inlining the four fields we touch
 * keeps the icon-set packages to data-only dependencies.
 */
export interface IconSetJson {
  /** Fallback dimensions for icons that do not carry their own. */
  readonly width?: number;
  readonly height?: number;
  readonly icons?: Readonly<
    Record<string, { readonly body?: string; readonly width?: number; readonly height?: number }>
  >;
}

export interface PointIconEntry {
  /** Namespaced symbol id, e.g. `maki:bar`. */
  readonly id: string;
  readonly setId: string;
  readonly setLabel: string;
  readonly name: string;
  readonly label: string;
  readonly width: number;
  readonly height: number;
  /** Inner SVG markup, still using `currentColor`. */
  readonly body: string;
  /** Pre-lowercased haystack for the picker's search box. */
  readonly search: string;
}

const ICON_SETS: ReadonlyArray<{
  readonly id: string;
  readonly label: string;
  readonly load: () => Promise<{ readonly icons: IconSetJson }>;
}> = [
  { id: 'maki', label: 'Maki', load: () => import('@iconify-json/maki') },
  { id: 'temaki', label: 'Temaki', load: () => import('@iconify-json/temaki') },
];

/**
 * Maki ships `-11` / `-15` pixel-grid variants of most icons. They are the same
 * artwork at another size, so listing them would only pad the picker with
 * near-duplicates.
 */
const SIZE_VARIANT_SUFFIX = /-(?:11|15)$/;

let loadPromise: Promise<ReadonlyArray<PointIconEntry>> | null = null;
const entriesById = new Map<string, PointIconEntry>();

export function pointIconIdFor(setId: string, name: string): string {
  return `${setId}:${name}`;
}

/** Namespaced ids address the icon sets; bare ids are built-in shapes. */
export function isPointIconId(symbolId: string): boolean {
  return symbolId.includes(':');
}

/** `car-rental` → `Car Rental`. */
export function pointIconLabel(name: string): string {
  return name
    .split(/[-_]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function entriesFromIconSet(
  setId: string,
  setLabel: string,
  json: IconSetJson,
): PointIconEntry[] {
  const defaultWidth = json.width ?? 16;
  const defaultHeight = json.height ?? 16;
  const entries: PointIconEntry[] = [];
  for (const [name, icon] of Object.entries(json.icons ?? {})) {
    if (SIZE_VARIANT_SUFFIX.test(name)) continue;
    if (typeof icon?.body !== 'string' || icon.body.length === 0) continue;
    const label = pointIconLabel(name);
    entries.push({
      id: pointIconIdFor(setId, name),
      setId,
      setLabel,
      name,
      label,
      width: icon.width ?? defaultWidth,
      height: icon.height ?? defaultHeight,
      body: icon.body,
      search: `${label} ${name} ${setId}`.toLowerCase(),
    });
  }
  entries.sort((a, b) => a.label.localeCompare(b.label));
  return entries;
}

export function loadPointIcons(): Promise<ReadonlyArray<PointIconEntry>> {
  loadPromise ??= (async () => {
    const perSet = await Promise.all(
      ICON_SETS.map(async (set) => {
        try {
          const mod = await set.load();
          return entriesFromIconSet(set.id, set.label, mod.icons);
        } catch (err) {
          // A missing set degrades the picker rather than breaking symbology.
          console.warn(`[pointIconRegistry] icon set "${set.id}" failed to load`, err);
          return [] as PointIconEntry[];
        }
      }),
    );
    const entries = perSet.flat();
    for (const entry of entries) entriesById.set(entry.id, entry);
    return entries;
  })();
  return loadPromise;
}

/**
 * Entry for an id when the sets are already in memory. Lets the synchronous
 * engine style paths draw an icon immediately, falling back to a plain shape on
 * the frame before the sets finish loading.
 */
export function peekPointIcon(symbolId: string): PointIconEntry | null {
  return entriesById.get(symbolId) ?? null;
}

export interface PointIconSearchResult {
  readonly visible: ReadonlyArray<PointIconEntry>;
  readonly total: number;
}

/**
 * Filter by every whitespace-separated term, capped at `limit` so a broad query
 * cannot mount hundreds of DOM nodes. `total` is the uncapped match count so
 * the picker can say how many were hidden.
 */
export function searchPointIcons(
  entries: ReadonlyArray<PointIconEntry>,
  query: string,
  limit = 180,
): PointIconSearchResult {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches =
    terms.length === 0
      ? entries
      : entries.filter((entry) => terms.every((term) => entry.search.includes(term)));
  return { visible: matches.slice(0, limit), total: matches.length };
}

export function pointIconSvgMarkup(entry: PointIconEntry, color: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${entry.width} ${entry.height}" ` +
    `width="${entry.width}" height="${entry.height}">` +
    `${entry.body.replaceAll('currentColor', color)}</svg>`
  );
}

/** Inline an SVG document so it can be used as an `<img>` / `ol/style/Icon` src. */
export function svgDataUrl(markup: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
}

export function pointIconDataUrl(entry: PointIconEntry, color: string): string {
  return svgDataUrl(pointIconSvgMarkup(entry, color));
}

/** Test seam — drops the cached sets so a suite can re-stub the imports. */
export function resetPointIconRegistryForTests(): void {
  loadPromise = null;
  entriesById.clear();
}
